/**
 * Whose contact details the "I will be onsite" checkbox fills in.
 *
 * Legacy resolved one `targetUser` up front (`TargetOrderUser.context.ts`): the
 * looked-up client when an admin orders on behalf, otherwise the logged-in
 * user. The meeting prefill then read `name`/`email`/`phone` off that single
 * object, so an admin filling the contact got the CLIENT's details.
 *
 * v2 split those two identities across a store slice and a user context, and
 * only the email survived the crossing — so an admin got the client's email
 * next to two empty fields. The decision is a pure function of the two
 * identities, kept here so it can be tested without rendering the step.
 */

import type { MeetingDTO } from 'models/purchaseFlowRevamp'

/** A meeting contact, as far as it could be resolved. Absent fields are left alone. */
export interface ContactPrefill {
  name?: string
  phone?: string
  email?: string
}

/** Which identity the order is being placed for. */
export type PrefillTarget = 'ME' | 'OTHER_CLIENT'

export interface ContactPrefillInput {
  /** Admin-only: the client the admin acts for, from `propertySlice`. */
  targetUserEmail?: string | null
  targetUserName?: string | null
  targetUserPhone?: string | null
  /** The logged-in user (`useUserData().baseUserData`). */
  user?: { name?: string | null, phone?: string | null, email?: string | null } | null
}

/**
 * `OTHER_CLIENT` only when an admin target is set AND it is somebody else.
 *
 * The email comparison is legacy's own test: an admin may legitimately order
 * for themselves, and that is not an on-behalf order.
 */
export const resolvePrefillTarget = ({ targetUserEmail, user }: ContactPrefillInput): PrefillTarget =>
  targetUserEmail && user?.email && targetUserEmail !== user.email ? 'OTHER_CLIENT' : 'ME'

/**
 * Legacy's `on_site_contact_is_orderer`: the client said they meet the creative themselves, for the two modes
 * that ask it (on site, key pickup); null for the others. An admin ordering for someone else is never it.
 */
export const onSiteContactIsOrderer = (meeting: MeetingDTO | undefined, prefillTarget: PrefillTarget): boolean | null => {
  if (meeting?.mode !== 'ON_SITE' && meeting?.mode !== 'KEY_PICKUP') return null
  return prefillTarget === 'ME' && meeting.contactIsSelf === true
}

/**
 * The contact details to prefill, from whichever identity owns the order.
 *
 * @example
 * resolveContactPrefill({ targetUserEmail: 'c@x.com', targetUserName: 'Client', user: me })
 * // → { name: 'Client', phone: undefined, email: 'c@x.com' }
 */
export const resolveContactPrefill = (input: ContactPrefillInput): ContactPrefill => {
  if (resolvePrefillTarget(input) === 'OTHER_CLIENT') {
    return {
      name: input.targetUserName ?? undefined,
      phone: input.targetUserPhone ?? undefined,
      email: input.targetUserEmail ?? undefined,
    }
  }
  return {
    name: input.user?.name ?? undefined,
    phone: input.user?.phone ?? undefined,
    email: input.user?.email ?? undefined,
  }
}
